<template>
  <transition name="modal">
    <div class="modal-mask manage-screens">
      <div class="modal-wrapper" data-test="production-outputsModal" v-on:click.self="$emit('close')">
        <div class="modal-container modal-container--fit">
          <div class="modal-header">
            <div class="title">Manage Outputs</div>
            <button
              class="modal-close tb-btn tb-btn-text sm low-em"
              data-test="production-outputsModal-close"
              @click.stop.prevent="$emit('close')"
            >
              <span class="icon icon-cross" />
            </button>
          </div>

          <div class="modal-body">
            <div class="tabs-component">
              <div class="tabs-header">
                <div
                  class="tab-title"
                  :class="{'active' : showUrlTab}"
                  data-test="production-outputsModal-connectUrlTab"
                  @click="switchTab('url')"
                >
                  Connect Using URL
                </div>

                <div class="tab-title"
                  :class="{'active' : showScreenTab}"
                  data-test="production-outputsModal-connectCodeTab"
                  @click="switchTab('screen')"
                >
                  Connect Using Code
                </div>
              </div>

              <div class="tab-body clearfix">
                <template v-if="showUrlTab">
                  <div class="tb-form-group">
                    <label
                      class="tb-label"
                      for="screenName"
                    >
                      Screen Name
                    </label>

                    <input
                      id="screenName"
                      class="tb-input"
                      v-model="url_title"
                      type="text"
                      name="url_title"
                      placeholder="Jumbotron"
                      data-test="production-outputsModal-outputNameInput"
                    >
                  </div>

                  <div
                    class="tb-btn tb-btn-contained high-em pull-right"
                    :disabled="isFormInvalid"
                    :class="{ 'disabled': isFormInvalid }"
                    data-test="production-outputsModal-createOutputButton"
                    @click="generateScreen"
                  >
                    Create display URL
                  </div>
                </template>

                <template v-if="showScreenTab">
                  <p class="margin-bottom-lg">
                    From your output device, visit
                    <strong><a href="http://tagboard.tv" target="_blank" rel="noopener noreferrer">tagboard.tv</a></strong>
                    and enter the code given to you below:
                  </p>

                  <div class="tb-form-group">
                    <label class="tb-label" for="screenCode">
                      Connect Code
                    </label>

                    <input
                      id="screenCode"
                      class="tb-input lighter"
                      v-model="screen_id"
                      type="text"
                      name="screen_id"
                      placeholder="abcd1234"
                      data-test="production-outputsModal-codeInput"
                    >
                  </div>

                  <div class="tb-form-group">
                    <label class="tb-label" for="screenNameByCode">
                      Screen Name
                    </label>

                    <input
                      id="screenNameByCode"
                      class="tb-input lighter"
                      v-model="screen_title"
                      type="text"
                      name="screen_title"
                      placeholder="Jumbotron"
                      data-test="production-outputsModal-outputNameInput"
                    >
                  </div>

                  <div
                    class="tb-btn tb-btn-contained high-em pull-right"
                    :disabled="isFormInvalid"
                    :class="{ 'disabled': isFormInvalid }"
                    data-test="production-outputsModal-connectCodeButton"
                    @click="connectScreen"
                  >
                    Connect
                  </div>
                </template>
              </div>
            </div>

            <hr>

            <div class="status-help-text margin-bottom-lg">
              <div class="producer-settings__description margin-bottom-xs">
                <span class="status-indicator" />
                Active and ready to be used
              </div>

              <div class="producer-settings__description">
                <span class="status-indicator status-indicator--disconnected" />
                Inactive (e.g. no network connection, output browser closed)
              </div>
            </div>

            <!-- Loader -->
            <div v-if="isLoading" class="loading loading--inline">
              <i class="icon icon-spinner"></i>
            </div>

            <!-- Screen Lists -->
            <div
              v-else
              class="screen-list clearfix"
            >
              <div class="production-screens">
                <div class="flex items-center margin-bottom-sm">
                  <h2 class="flex-grow margin-bottom-none tb-label">This Production</h2>

                  <button
                    v-if="connectedScreens && connectedScreens.length"
                    class="tb-btn tb-btn-text medium-em sm"
                    title="Force any connected outputs to reload. This could cause disruptions."
                    data-test="production-outputsModal-reloadAllScreensButton"
                    @click="showReloadConfirmation = true"
                  >
                    <i class="fas fa-sync"></i>
                    Reload Connected Screens
                  </button>
                </div>

                <draggable
                  class="screens-draggable draggable"
                  :class="productionScreens.length ? '' : 'screens-draggable--empty'"
                  v-bind="{ sort:false, animation: 200, group: { name: 'screens' }}"
                  revert-on-spill="true"
                  data-test="producer-activeOutputScreens-droparea"
                  @add="productionScreenChange"
                >
                  <card-component
                    v-for="screen in productionScreens"
                    type="screen"
                    :key="screen._id"
                    :card="screen"
                    :data-id="screen._id"
                    :hide-production-title="true"
                    :data-test="`production-outputsModal-outputs-${screen._id}`"
                    @save="onSave"
                    @reload="reloadScreen(screen._id)"
                    @delete="deleteConfirm"
                  />
                </draggable>

                <div slot="footer" class="screens-droparea droparea text-center">
                  <i class="icon icon-move-down"></i>
                </div>
              </div>

              <div class="other-screens">
                <div class="margin-bottom-sm">
                  <h2 class="margin-bottom-none tb-label">Other Screens</h2>
                </div>

                <draggable
                  class="screens-draggable draggable"
                  :class="otherScreens.length ? '' : 'screens-draggable--empty'"
                  v-bind="{ sort: false, animation: 200, group: { name: 'screens' } }"
                  revert-on-spill="true"
                  data-test="producer-outputsModal-otherOutputs-droparea"
                  @add="otherScreenChange"
                >
                  <card-component
                    type="screen"
                    :data-id="screen._id"
                    v-for="screen in otherScreens"
                    :key="screen._id"
                    :card="screen"
                    :data-test="`production-outputsModal-outputs-${screen._id}`"
                    @save="onSave"
                    @delete="deleteConfirm"
                  />
                </draggable>

                <div slot="footer" class="screens-droparea droparea text-center">
                  <i class="icon icon-move-down"></i>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <alert
        v-if="showAlert"
        footerConfirm="true"
        @confirm="deleteScreen"
        @close="showAlert = false"
      >
        <h2>Are you sure?</h2>
        <p>This will delete this screen from this production and reset.</p>
      </alert>

      <alert
        v-if="showReloadConfirmation"
        footerConfirm="true"
        @confirm="reloadScreens"
        @close="showReloadConfirmation = false"
      >
        <h2>Are you sure?</h2>

        <p>
          This could cause temporary disruptions with your outputs,
          and should be used whenever an output isn't working properly.
        </p>
      </alert>

      <alert
        v-if="showDisplayUrlSuccessConfirmation"
        footerClose="true"
        @close="closeSuccessModal"
      >
        <h2>Your display URL has been created successfully.</h2>

        <p class="margin-bottom-lg">
          To view your display,
          click the button below to copy the display URL and paste it into your browser.
        </p>

        <copy-clipboard
          class="tb-btn tb-btn-contained high-em"
          data-test="production-outputsModal-newUrlCopyButton"
          :data-clipboard-text="displayUrl"
        >
          <span class="icon icon-link margin-right-xs"></span>
          Copy display URL
        </copy-clipboard>
      </alert>
    </div>
  </transition>
</template>

<script>
import draggable from 'vuedraggable';

import {
  mapActions,
  mapGetters,
  mapState,
} from 'vuex';

import ConduitEventsService from '../../services/conduitEventsService';

import AlertComponent from '../alert/Alert.vue';
import CardComponent from '../card/Card.vue';
import CopyClipboard from '../../shared/copy-clipboard/CopyClipboard.vue';

export default {
  components: {
    'card-component': CardComponent,
    alert: AlertComponent,
    draggable,
    'copy-clipboard': CopyClipboard,
  },

  data() {
    return {
      screen_id: '',
      screen_title: '',
      url_title: '',
      selectedScreen: null,
      showAlert: false,
      showUrlTab: true,
      showScreenTab: false,
      showDisplayUrlSuccessConfirmation: false,
      showReloadConfirmation: false,
      forceReload: new Set(),
      forceReloadTimeouts: {},
    };
  },

  mounted() {
    this.load();
  },

  watch: {
    // If a new screen is linked, then show modal to copy code
    linkedScreen(val) {
      this.selectedScreen = val;
      this.showDisplayUrlSuccessConfirmation = !!val;
    },

    connectedScreens(screens) {
      if (screens.length && this.forceReload.size) {
        // Force a newly connected screen to reload, then remove
        // the ID from the forceReload set
        this.forceReload.forEach((id) => {
          const screen = screens.find((s) => s.screen_id === id);
          if (screen) {
            this.reloadScreen(id);
            this.forceReload.delete(id);
          }
        });
      }
    },
  },

  beforeDestroy() {
    clearInterval(this.connectedScreensInterval);
  },

  methods: {
    ...mapActions([
      'getScreens',
      'checkConnectedScreens',
    ]),

    closeSuccessModal() {
      // Will loop back around to watcher to close the modal
      this.$store.dispatch('setLinkedScreen', null);
    },

    async load() {
      // Get list of connected screens for the current production
      this.checkConnectedScreens(this.production.id);

      // Fetch list of all screens for the current owner
      try {
        await this.getScreens();
      } catch (err) {
        console.error('Error getting screens:', err);
        this.errorToast('There was an error getting your connected screens. Please try again.');
      }
    },

    reloadScreens() {
      this.$socket.emit('reloadScreens', { production_id: this.production.id });
      this.showReloadConfirmation = false;
    },

    reloadScreen(screenId) {
      const screen = this.connectedScreens.find((s) => s.screen_id === screenId);

      if (screen) {
        this.$socket.emit('reloadScreens', {
          production_id: this.production.id,

          // Pull list of ALL clients connected to current screen
          // because this could be multiple windows
          client_ids: this.connectedScreens.reduce((memo, s) => {
            if (s.screen_id === screenId) {
              return memo.concat([s.client_id]);
            }

            return memo;
          }, []),
        });
      }
    },

    async productionScreenChange(evt) {
      const id = evt.item?.dataset?.id;

      try {
        await this.$store.dispatch('changeScreenProductionId', id);
      } catch (err) {
        this.errorToast('There was an error getting your connected screens. Please try again.');
      }

      this.setScreens(0, true);

      this.forceReload.add(id);
      this.reloadOnConnect(id);
    },

    /**
    * Temporarily store screenId
    * so we can reload the screen if it's already connected to some other production.
    * If we don't connect to the screen within X seconds,
    * then we'll just forget about force reloading it.
    * @param {string} screenId
    */
    reloadOnConnect(screenId) {
      clearTimeout(this.forceReloadTimeouts[screenId]);
      this.forceReload.add(screenId);
      this.forceReloadTimeouts[screenId] = setTimeout(() => {
        this.forceReload.delete(screenId);
      }, 10e3);
    },

    otherScreenChange(evt) {
      const id = evt.item?.dataset?.id;

      this.$store.dispatch('removeScreenProductionId', id)
        .then(() => {
          this.setScreens(0, true);
        }).catch(() => {
          this.errorToast('There was an error updating your screens. Please try again.');
        });
    },

    setScreens(depth = 0, draggedScreen = false) {
      if (draggedScreen) {
        return this.$store.dispatch('setScreens');
      }

      if (!this.$store.state.screens.screensReady && depth <= 5) {
        return new Promise((resolve) => {
          // Setting 1000 ms time
          setTimeout(resolve, 1000);
        }).then(() => this.setScreens(depth + 1, draggedScreen));
      }

      return this.$store.dispatch('setScreens');
    },

    async connectScreen() {
      if (this.isFormInvalid) {
        return;
      }

      const {
        _id: productionId,
        owner,
      } = this.production;

      try {
        await this.$store.dispatch('createScreen', {
          owner,
          production_id: productionId,
          screen_id: this.screen_id,
          title: this.screen_title,
        });
      } catch (err) {
        this.errorToast('Failed to create screen');
        console.error(err); // eslint-disable-line no-console
        return;
      }

      ConduitEventsService.track(
          'producer.created.screen',
          {
            production_id: this.production._id,
            screen_id: this.screen_id,
          },
          {
            ownerId: this.$store.state.production.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );

      this.$store.dispatch('connectScreen', {
        owner,
        production_id: productionId,
        screen_id: this.screen_id,
        display_token: this.$store.state.production.production.display_token,
      });

      ConduitEventsService.track(
          'producer.connected.screen',
          {
            production_id: this.production._id,
            screen_id: this.screen_id,
          },
          {
            ownerId: this.$store.state.production.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );

      // Clear state
      this.screen_id = '';
      this.screen_title = '';
    },

    generateScreen() {
      if (this.isFormInvalid) {
        return;
      }

      this.$store.dispatch(
        'generateScreen',
        {
          production_id: this.production._id,
          display_token: this.$store.state.production.production.display_token,
          owner: this.$store.state.production.production.owner,
          title: this.url_title,
        },
      );

      // Clear inputs after creating new screen
      this.url_title = '';
      this.screen_id = '';
      this.screen_title = '';

      ConduitEventsService.track(
          'producer.generated.screen',
          {
            production_id: this.production._id,
            display_token: this.$store.state.production.production.display_token,
            screen_title: this.url_title,
            screen_id: this.screen_id,
            owner: this.$store.state.production.production.owner,
          },
          {
            ownerId: this.$store.state.production.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    switchTab(tab) {
      if (tab === 'url') {
        this.showUrlTab = true;
        this.showScreenTab = false;
      } else if (tab === 'screen') {
        this.showUrlTab = false;
        this.showScreenTab = true;
      }
    },

    async onSave(screen) {
      try {
        await this.$store.dispatch('editScreen', screen);
      } catch (error) {
        this.errorToast('There was an error saving your screen title. Please try again.');
        return;
      }

      ConduitEventsService.track(
          'producer.edited.screen',
          {
            production_id: screen.production_id,
            screen_id: screen._id,
            screen,
          },
          {
            ownerId: this.$store.state.production.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    deleteConfirm(screen) {
      this.selectedScreen = screen;
      this.showAlert = true;
    },

    async deleteScreen() {
      try {
        await this.$store.dispatch('deleteScreen', this.selectedScreen);
      } catch (err) {
        this.errorToast('There was an error deleting this screen. Please try again.');
      }

      this.showAlert = false;
      this.selectedScreen = null;
    },
  },

  computed: {
    ...mapGetters(['productionScreens', 'otherScreens']),

    ...mapState({
      user: (state) => state.user,
      production: (state) => state.production.production,
      screens: (state) => state.screens.screen,
      isLoading: (state) => state.screens.isLoading[state.production.production.owner],
      connectedScreens: (state) => state.screens.connectedScreens,
      linkedScreen: (state) => state.screens.linkedScreen,
    }),

    isFormInvalid() {
      if (this.showUrlTab) {
        return (
          !this.url_title.trim()
          || this.url_title.trim().length < 4
        );
      }

      return (
        !this.screen_id.trim()
        || !this.screen_title.trim()
        || this.screen_id.trim().length < 5
        || this.screen_title.trim().length < 4
      );
    },

    displayUrl() {
      return `${process.env.LIVE_URL}/#/${this.selectedScreen.token}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.screen-list .tb-label {
  margin-bottom: 0;
}

.status-indicator {
  display: inline-block;
  margin-right: 0.25rem;
  width: 8px;
  height: 8px;
  border-radius: 100%;
  background-color: #00D483;

  &--disconnected {
    background-color: #e65447;
  }
}

.screens-draggable--empty {
  /* Add space to empty draggable so output can be dropped */
  padding-bottom: 48px;
  margin-bottom: -48px; /* Pull the "empty" state up */
  z-index: 1;
  position: relative;
}
</style>
